import React, { useState, useEffect, useCallback } from 'react';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import { dexscreenerApi, DexScreenerTokenResponse } from '../api/dexscreener-api';
import { useToasterStore } from '../stores/ui/toasterStore';
import { TokenCategory } from '../stores/data/types';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import { formatDateTimeForKstInput, parseLocalDateTimeToUtcDate } from '../utils/time';
import DateTime24Picker from './DateTime24Picker';

const TOKEN_CATEGORY_OPTIONS = Object.values(TokenCategory) as TokenCategory[];

interface CreateNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  tokenSymbol: string;
  tokenAddress: string;
  tokenName?: string;
  tokenTwitter?: string;
  tokenWebsite?: string;
  tokenCategory?: TokenCategory;
  syncId?: string;
  existingAssociatedHandles?: string[];
  existingCommunityId?: string;
  onSuccess?: (syncId: string) => void;
}

type SourceTypeOption = 'X_TWEET' | 'X_ACCOUNT' | 'X_COMMUNITY';

interface SocialSourceFormItem {
  id: string;
  sourceType: SourceTypeOption;
  sourceKey: string;
  from: string;
  to: string;
}

const SOURCE_TYPE_OPTIONS: SourceTypeOption[] = ['X_TWEET', 'X_ACCOUNT', 'X_COMMUNITY'];

const createEmptySocialSource = (): SocialSourceFormItem => ({
  id: `${Date.now()}-${Math.random()}`,
  sourceType: 'X_TWEET',
  sourceKey: '',
  from: '',
  to: formatDateTimeForKstInput(new Date()),
});

const normalizeSourceType = (value: unknown): SourceTypeOption | null => {
  if (value === 'X_TWEET' || value === 'X_ACCOUNT' || value === 'X_COMMUNITY') {
    return value;
  }
  return null;
};

const getSourceExternalUrl = (
  sourceType: SourceTypeOption,
  sourceKey: string,
): string | null => {
  const normalizedKey = sourceKey.trim();
  if (!normalizedKey) return null;

  if (sourceType === 'X_TWEET') {
    return `https://x.com/i/status/${normalizedKey}`;
  }
  if (sourceType === 'X_COMMUNITY') {
    return `https://x.com/i/communities/${normalizedKey}`;
  }
  return `https://x.com/${normalizedKey.replace(/^@/, '')}`;
};

const toSocialSourceId = () => `${Date.now()}-${Math.random()}`;

const parseCandidateUrls = (value?: string): string[] => {
  if (!value) return [];
  const matched = value.match(/https?:\/\/[^\s,]+/gi) ?? [];
  return matched;
};

const normalizeXHost = (host: string): boolean => {
  const lowered = host.toLowerCase();
  return lowered === 'x.com' || lowered === 'www.x.com' || lowered === 'twitter.com' || lowered === 'www.twitter.com';
};

const parseSocialSourceFromUrl = (
  rawUrl: string,
  communityFrom: string,
  defaultTo: string,
): SocialSourceFormItem | null => {
  try {
    const parsed = new URL(rawUrl.trim());
    if (!normalizeXHost(parsed.hostname)) return null;

    const pathParts = parsed.pathname.split('/').filter(Boolean);
    if (pathParts.length === 0) return null;

    // Tweet URL: /{handle}/status/{tweetId}
    if (pathParts.length >= 3 && pathParts[1].toLowerCase() === 'status' && /^\d+$/.test(pathParts[2])) {
      return {
        id: toSocialSourceId(),
        sourceType: 'X_TWEET',
        sourceKey: pathParts[2],
        from: '',
        to: defaultTo,
      };
    }

    // Community URL: /i/communities/{communityId}
    if (
      pathParts.length >= 3 &&
      pathParts[0].toLowerCase() === 'i' &&
      pathParts[1].toLowerCase() === 'communities' &&
      /^\d+$/.test(pathParts[2])
    ) {
      return {
        id: toSocialSourceId(),
        sourceType: 'X_COMMUNITY',
        sourceKey: pathParts[2],
        from: communityFrom,
        to: defaultTo,
      };
    }

    // Account profile URL: /{handle}
    if (pathParts.length >= 1) {
      const handle = pathParts[0].replace('@', '');
      if (handle && !['i', 'home', 'explore', 'search', 'intent', 'share'].includes(handle.toLowerCase())) {
        return {
          id: toSocialSourceId(),
          sourceType: 'X_ACCOUNT',
          sourceKey: handle,
          from: '',
          to: defaultTo,
        };
      }
    }
  } catch (_error) {
    return null;
  }

  return null;
};

const buildPrefilledSocialSources = (
  twitterValue: string | undefined,
  websiteValue: string | undefined,
  communityFrom: string,
): SocialSourceFormItem[] => {
  const defaultTo = formatDateTimeForKstInput(new Date());
  const candidates = [...parseCandidateUrls(twitterValue), ...parseCandidateUrls(websiteValue)];
  const parsed = candidates
    .map((url) => parseSocialSourceFromUrl(url, communityFrom, defaultTo))
    .filter((item): item is SocialSourceFormItem => item !== null);

  const dedup = new Map<string, SocialSourceFormItem>();
  for (const item of parsed) {
    dedup.set(`${item.sourceType}:${item.sourceKey}`, item);
  }
  return Array.from(dedup.values());
};

const CreateNotesModalComponent: React.FC<CreateNotesModalProps> = ({
  isOpen,
  onClose,
  tokenSymbol,
  tokenAddress,
  tokenName,
  tokenTwitter,
  tokenWebsite,
  tokenCategory,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [projectSiteUrl, setProjectSiteUrl] = useState('');
  const [socialSources, setSocialSources] = useState<SocialSourceFormItem[]>([createEmptySocialSource()]);
  const [selectedCategory, setSelectedCategory] = useState<TokenCategory>(tokenCategory ?? TokenCategory.NONE);
  const [isLoadingTokenState, setIsLoadingTokenState] = useState(false);
  const [, setDexscreenerData] = useState<DexScreenerTokenResponse | null>(null);
  const { showToaster } = useToasterStore();

  const isFormDisabled = isLoading || isLoadingTokenState;
  const hasIncompleteSocialSource = socialSources.some(
    (item) =>
      !item.sourceKey.trim() ||
      !item.from.trim() ||
      !item.to.trim(),
  );
  const isSyncButtonDisabled = isFormDisabled || hasIncompleteSocialSource;

  const loadTokenState = useCallback(async () => {
    if (!tokenAddress) return;

    setIsLoadingTokenState(true);
    try {
      // Dexscreener API 직접 호출 (메타데이터 파싱 전)
      const dexData = await dexscreenerApi.getTokenPairs(tokenAddress);
      setDexscreenerData(dexData);
      console.log('[CreateNoteModal] DexScreener pairs:', dexData);

      try {
        await feManagementSystemApi.refreshTokenNoteFromDexScreener(tokenAddress);
      } catch (error) {
        console.error('DexScreener refresh before token state failed:', error);
      }

      const tokenState = await feManagementSystemApi.getTokenState(tokenAddress);
      setSelectedCategory(tokenState.category ?? tokenCategory ?? TokenCategory.NONE);

      const note = tokenState.note as any;
      setProjectSiteUrl(note?.projectSiteUrl || '');

      const rawSocialSources = note?.socialSources ?? note?.social_sources ?? [];
      if (Array.isArray(rawSocialSources) && rawSocialSources.length > 0) {
        const parsed = rawSocialSources
          .map((item: any): SocialSourceFormItem | null => {
            const sourceType = normalizeSourceType(item?.sourceType ?? item?.source_type);
            if (!sourceType) return null;
            return {
              id: `${Date.now()}-${Math.random()}`,
              sourceType,
              sourceKey: String(item?.sourceKey ?? item?.source_key ?? ''),
              from: item?.from ? formatDateTimeForKstInput(item.from) : '',
              to: item?.to ? formatDateTimeForKstInput(item.to) : formatDateTimeForKstInput(new Date()),
            };
          })
          .filter((item: SocialSourceFormItem | null): item is SocialSourceFormItem => item !== null);
        setSocialSources(parsed.length > 0 ? parsed : [createEmptySocialSource()]);
      } else {
        const communityFrom = note?.contentsReferencedFrom
          ? formatDateTimeForKstInput(note.contentsReferencedFrom)
          : '';
        const prefilled = buildPrefilledSocialSources(
          tokenTwitter,
          tokenWebsite ?? note?.projectSiteUrl,
          communityFrom,
        );
        setSocialSources(prefilled.length > 0 ? prefilled : [createEmptySocialSource()]);
      }
    } catch (error) {
      console.error('Failed to load token state for note modal:', error);
      // Even if API calls fail, keep URL-based prefill working.
      setSelectedCategory(tokenCategory ?? TokenCategory.NONE);
      setProjectSiteUrl('');
      const prefilled = buildPrefilledSocialSources(
        tokenTwitter,
        tokenWebsite,
        '',
      );
      setSocialSources(prefilled.length > 0 ? prefilled : [createEmptySocialSource()]);
    } finally {
      setIsLoadingTokenState(false);
    }
  }, [tokenAddress, tokenCategory, tokenTwitter, tokenWebsite]);

  useEffect(() => {
    if (isOpen) {
      loadTokenState();
    }
  }, [isOpen, loadTokenState]);

  useEffect(() => {
    if (!isOpen) {
      setProjectSiteUrl('');
      setSocialSources([createEmptySocialSource()]);
      setSelectedCategory(tokenCategory ?? TokenCategory.NONE);
    }
  }, [isOpen, tokenCategory]);

  const updateSocialSource = (id: string, patch: Partial<SocialSourceFormItem>) => {
    setSocialSources((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const addSocialSource = () => {
    setSocialSources((prev) => [...prev, createEmptySocialSource()]);
  };

  const removeSocialSource = (id: string) => {
    setSocialSources((prev) => {
      const next = prev.filter((item) => item.id !== id);
      return next.length > 0 ? next : [createEmptySocialSource()];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (selectedCategory === TokenCategory.NONE) {
      showToaster('Please select a category.', 'error');
      setIsLoading(false);
      return;
    }

    try {
      const normalizedSources = socialSources
        .map((item) => ({
          sourceType: item.sourceType,
          sourceKey: item.sourceKey.trim(),
          from: parseLocalDateTimeToUtcDate(item.from),
          to: parseLocalDateTimeToUtcDate(item.to),
        }))
        .filter((item) => item.sourceKey || item.from || item.to);

      if (normalizedSources.length === 0) {
        showToaster('최소 1개의 Social Source를 입력해주세요.', 'error');
        return;
      }

      for (const source of normalizedSources) {
        if (!source.sourceKey) {
          showToaster('Social Source의 Source Key를 입력해주세요.', 'error');
          return;
        }
        if (!source.from || !source.to) {
          showToaster('Social Source의 From/To를 모두 입력해주세요.', 'error');
          return;
        }
        if (source.from > source.to) {
          showToaster('Social Source의 From은 To보다 늦을 수 없습니다.', 'error');
          return;
        }
      }

      await feManagementSystemApi.submitForContentGeneration(tokenAddress, {
        category: selectedCategory,
        projectSiteUrl: projectSiteUrl.trim() || null,
        socialSources: normalizedSources.map((item) => ({
          sourceType: item.sourceType,
          sourceKey: item.sourceKey,
          from: item.from as Date,
          to: item.to as Date,
        })),
      });

      showToaster('Fetching tweet data...', 'success');
      if (onSuccess) onSuccess(tokenAddress);
      onClose();
    } catch (error: any) {
      console.error('컨텐츠 생성 정보 제출 실패:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to submit content creation info. Please try again.'),
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="mb-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-white">
                {'Create Notes (Social Research)'}
              </h2>
              <p className="text-gray-400 text-sm mt-1">소셜 리서치를 위한 노트를 생성하세요.</p>
              <p className="text-gray-400 text-sm mt-1">{tokenAddress} / {tokenSymbol} ({tokenName})</p>
            </div>
            <button
              type="button"
              onClick={() =>
                window.open(
                  `https://gmgn.ai/sol/token/solscan_${tokenAddress}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-600 px-2.5 py-1.5 text-xs font-medium text-gray-200 hover:bg-gray-700"
              title="Open GMGN in new tab"
            >
              <img src="/gmgn.png" alt="gmgn" className="h-3.5 w-3.5" />
              GMGN
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
            <h3 className="text-sm font-semibold text-pink-400 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-pink-400 rounded-full" />
              Category
            </h3>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as TokenCategory)}
              disabled={isFormDisabled}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {TOKEN_CATEGORY_OPTIONS.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
            <h3 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full" />
              Project Site URL (선택사항)
            </h3>
            <input
              type="url"
              placeholder="https://example.com"
              value={projectSiteUrl}
              onChange={(e) => setProjectSiteUrl(e.target.value)}
              disabled={isFormDisabled}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-blue-400 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full" />
                Social Sources
              </h3>
              <button
                type="button"
                onClick={addSocialSource}
                disabled={isFormDisabled}
                className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-500 rounded-md text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                + Add Source
              </button>
            </div>
            <div className="space-y-3">
              {socialSources.map((source, index) => (
                <div key={source.id} className="rounded-md border border-gray-600 bg-gray-800/60 p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-300 font-semibold">Source #{index + 1}</p>
                      {source.sourceKey.trim() && (
                        <button
                          type="button"
                          onClick={() => {
                            const url = getSourceExternalUrl(source.sourceType, source.sourceKey);
                            if (!url) return;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                          className="px-2 py-1 text-xs rounded border border-blue-400/60 text-blue-300 hover:bg-blue-500/10"
                          title="Open source in new tab"
                        >
                          Open in new tab
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSocialSource(source.id)}
                      disabled={isFormDisabled}
                      className="px-2 py-1 text-xs rounded bg-red-600/80 hover:bg-red-500 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Source Type</label>
                      <select
                        value={source.sourceType}
                        onChange={(e) => updateSocialSource(source.id, { sourceType: e.target.value as SourceTypeOption })}
                        disabled={isFormDisabled}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {SOURCE_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-300 mb-1">Source Key</label>
                      <input
                        type="text"
                        value={source.sourceKey}
                        onChange={(e) => updateSocialSource(source.id, { sourceKey: e.target.value })}
                        placeholder="communityId / tweetId / account handle"
                        disabled={isFormDisabled}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <DateTime24Picker
                      value={source.from}
                      onChange={(next) => updateSocialSource(source.id, { from: next ?? '' })}
                      disabled={isFormDisabled}
                      dateLabel="From"
                    />
                    <DateTime24Picker
                      value={source.to}
                      onChange={(next) => updateSocialSource(source.id, { to: next ?? '' })}
                      disabled={isFormDisabled}
                      dateLabel="To"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSyncButtonDisabled}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? '동기화 중...' : 'Sync'}
            </button>
          </div>
        </form>

        {isLoadingTokenState && (
          <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

const CreateNoteModal = React.memo(
  CreateNotesModalComponent,
  (prevProps, nextProps) => prevProps.isOpen === nextProps.isOpen,
);

export default CreateNoteModal;
